import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Badge, Button, Input } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle, Search } from "lucide-react";
import { useMemo, useState } from "react";
import {
  useCommunicationNotificationsQuery,
  useMarkCommunicationNotificationReadMutation,
} from "../api/queries";
import { CommunicationTour } from "../tour/CommunicationTour";
import { notificationsTourSteps } from "../tour/communicationTourSteps";
import { useCommunicationTour } from "../tour/useCommunicationTour";

export function CommunicationNotificationsPage() {
  const tour = useCommunicationTour(
    "communication-notifications-tour-completed",
  );
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const query = useCommunicationNotificationsQuery();
  const markRead = useMarkCommunicationNotificationReadMutation();
  const items = query.data?.data ?? [];
  const normalizedSearch = search.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    if (!normalizedSearch) return items;
    return items.filter((item) =>
      [item.title, item.content, item.kind, item.link ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch),
    );
  }, [items, normalizedSearch]);

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex items-start justify-between gap-3">
          <div data-tourid="communication-notifications-title">
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight">ການແຈ້ງເຕືອນ</h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={tour.startTour}
                aria-label="ເລີ່ມທົດລອງການນຳທາງ"
                title="ເລີ່ມທົດລອງການນຳທາງ"
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
            <p className="mt-1 text-muted-foreground">
              ສູນກາງການແຈ້ງເຕືອນສ່ວນຕົວຂອງທ່ານ.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            className="shrink-0"
            onClick={() => navigate({ to: "/app/dashboard" })}
          >
            <ArrowLeft className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <div
          data-tourid="communication-notifications-list"
          className="divide-y divide-border"
        >
          <div className="py-4">
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="ຄົ້ນຫາການແຈ້ງເຕືອນ..."
              />
            </div>
          </div>

          {query.isLoading ? (
            Array.from(
              { length: 6 },
              (_, i) => `notification-skeleton-${i}`,
            ).map((key) => (
              <div
                key={key}
                className="h-24 animate-pulse rounded-md bg-muted/60 py-4"
              />
            ))
          ) : items.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">
              ບໍ່ມີການແຈ້ງເຕືອນ.
            </p>
          ) : filteredItems.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">
              ບໍ່ພົບຂໍ້ມູນຕາມຄຳຄົ້ນຫາ.
            </p>
          ) : (
            filteredItems.map((item) => (
              <div key={item.id} className="py-4">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-semibold text-lg">{item.title}</h3>
                  {item.isRead ? (
                    <Badge variant="outline">ອ່ານແລ້ວ</Badge>
                  ) : (
                    <Badge className="bg-black text-white hover:bg-black/90">
                      ຍັງບໍ່ອ່ານ
                    </Badge>
                  )}
                </div>
                <p className="mb-2 text-sm">{item.content}</p>
                <p className="mb-3 text-muted-foreground text-xs">
                  {item.createdAt}
                </p>
                {!item.isRead ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => markRead.mutate(item.id)}
                  >
                    ຫມາຍເຫດວ່າອ່ານແລ້ວ
                  </Button>
                ) : null}
              </div>
            ))
          )}
        </div>

        <CommunicationTour
          steps={notificationsTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
